/**
 * Parent wiring for directory map Views CRUD (ADR-MV-05).
 * Maps platform DTOs → ShopMapSavedViewRow; delete/rename mutate + invalidate.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  deleteMapCustomView,
  patchMapCustomView,
} from '../account/mapViewsApi.js';
import { useCustomerMapViews } from '../account/useCustomerMapViews.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import {
  shopMapSavedViewRowFromDto,
  type ShopMapSavedViewRow,
} from './shopMapViewModel.js';

export function useShopsDirectoryMapViews(): {
  readonly savedViews: readonly ShopMapSavedViewRow[];
  /**
   * True while signed-in map-views query is unsettled (`isPending`, no settled data).
   * Covers offline/paused first fetch (`isPending && !isFetching`) — not RQ `isLoading`
   * alone (`isPending && isFetching`), which false-clears restore to General.
   * Guests / disabled query / settled success|error → false (never false-General).
   */
  readonly savedViewsLoading: boolean;
  /** Signed-in list/restore failed (timeout/5xx/network) — not empty success. */
  readonly savedViewsError: boolean;
  readonly onRetrySavedViews: () => void;
  readonly deletePending: boolean;
  readonly renamePending: boolean;
  readonly onRequestDeleteView: (viewId: number) => void;
  readonly onRequestRenameView: (
    viewId: number,
    name: string,
  ) => Promise<void>;
} {
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const signedIn = isGloballySignedInChrome({ session });
  const queryClient = useQueryClient();
  const customerKey =
    session?.customerId ?? (signedIn ? 'session' : -1);
  const query = useCustomerMapViews(signedIn);
  const [deletePending, setDeletePending] = useState(false);
  const [renamePending, setRenamePending] = useState(false);

  // G2: isPending (not isLoading). Offline/paused first fetch is isPending with
  // isFetching=false → isLoading=false; treating that as settled [] false-Generals.
  // Guests: signedIn=false gates out disabled-query idle pending.
  const savedViewsLoading = signedIn && query.isPending;
  const savedViewsError = signedIn && query.isError;
  const onRetrySavedViews = useCallback((): void => {
    void query.refetch();
  }, [query]);

  const savedViews = useMemo(
    () => (query.data ?? []).map(shopMapSavedViewRowFromDto),
    [query.data],
  );

  const invalidate = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: customerQueryKeys.mapCustomViews(customerKey),
    });
  }, [customerKey, queryClient]);

  const onRequestDeleteView = useCallback(
    (viewId: number): void => {
      if (!signedIn || deletePending) {
        return;
      }
      setDeletePending(true);
      void (async () => {
        try {
          await deleteMapCustomView({ apiBaseUrl, id: viewId });
          await invalidate();
        } finally {
          setDeletePending(false);
        }
      })();
    },
    [apiBaseUrl, deletePending, invalidate, signedIn],
  );

  const onRequestRenameView = useCallback(
    async (viewId: number, name: string): Promise<void> => {
      if (!signedIn || renamePending) {
        return;
      }
      const row = savedViews.find((v) => v.id === viewId);
      if (row === undefined) {
        return;
      }
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        return;
      }
      setRenamePending(true);
      try {
        // Kind immutable — name-only BodyForKind<K> (EV-G1-PATCH-KIND).
        switch (row.kind) {
          case 'around_me':
            await patchMapCustomView<'around_me'>({
              apiBaseUrl,
              id: viewId,
              body: { name: trimmed },
            });
            break;
          case 'around_place':
            await patchMapCustomView<'around_place'>({
              apiBaseUrl,
              id: viewId,
              body: { name: trimmed },
            });
            break;
          case 'map_camera':
            await patchMapCustomView<'map_camera'>({
              apiBaseUrl,
              id: viewId,
              body: { name: trimmed },
            });
            break;
          case 'exact_bounds':
            await patchMapCustomView<'exact_bounds'>({
              apiBaseUrl,
              id: viewId,
              body: { name: trimmed },
            });
            break;
        }
        await invalidate();
      } finally {
        setRenamePending(false);
      }
    },
    [apiBaseUrl, invalidate, renamePending, savedViews, signedIn],
  );

  return {
    savedViews,
    savedViewsLoading,
    savedViewsError,
    onRetrySavedViews,
    deletePending,
    renamePending,
    onRequestDeleteView,
    onRequestRenameView,
  };
}
